import React, { FC, useCallback, useState } from 'react';
import { useDataTableColumnConfigurations } from 'utils/hooks/useDataTableColumnConfigurations/useDataTableColumnConfigurations';
import { useDarkly } from 'integrations/launchDarkly/legacy/useDarkly';
import { canDelete } from 'utils/permissions';
import { useStaticPermission, useStaticPermissions } from 'common/auth/Authorize';
import { JobFamilyGroupModel } from '@utmost.co/enterprise-models';
import {
  DataTable,
  DataTableBulkActions,
  DataTableToolbar,
  DeleteBulkAction,
  DataTablePaginator
} from '@utmost.co/utmost-design-system';
import { useDialogWithUids, IconButton } from '@beeline.com/bp-design-system';
import { Importer } from '@oc/pages/importers_rsi/Importer';
import { useJobFamilies } from './useJobFamilyGroups';
import { useJobFamilyGroupColumns } from './useJobFamilyGroupColumns';
import { JobFamilyGroupDialog } from './JobFamilyGroupDialog';
import { JobFamilyGroup } from './jobFamilyGroups.graphql';
import { JobFamilyGroupDeleteDialog } from './JobFamilyGroupDeleteDialog';
import { useJobFamilyGroupsIntl } from './translations/useJobFamilyGroupsIntl';
import { ImportEntityTypeModel } from 'src/graphql/types';
import { JobFamilyGroupsExporter } from '@oc/pages/settings/Engagements/JobCatalogues/jobFamilyGroups/JobFamilyGroupsExporter';

export const JobFamilyGroupsTable: FC<React.PropsWithChildren<unknown>> = () => {
  const intl = useJobFamilyGroupsIntl();
  const [selectedJobFamilyGroups, setSelectedJobFamilyGroups] = useState<JobFamilyGroup[]>([]);
  const [selectedWDJobFamilyGroups, setSelectedWDJobFamilyGroups] = useState<JobFamilyGroup[]>([]);
  const [isBulkAction, setIsBulkAction] = useState<boolean>(false);
  const [showJobFamilyGroupImport, setJobFamilyGroupImport] = useState(false);
  const [showJobFamilyGroupAdd, setJobFamilyGroupAdd] = useState(false);
  const [, setEditableJobFamilyGroup] = useState<JobFamilyGroupModel | null>(null);
  const { extendJobFamilyGroup } = useDarkly();
  const { columnConfiguration, patchColumnConfiguration } =
    useDataTableColumnConfigurations('ENGAGEMENTS_JOB_FAMILY_GROUP');
  const deleteDialog = useDialogWithUids([]);

  const staticPermissions = useStaticPermissions();
  const importJobFamilyGroupEnabled = staticPermissions({ permissionName: 'JOB_FAMILY_GROUP' }).IMPORT;
  const addJobFamilyGroupEnabled = staticPermissions({ permissionName: 'JOB_FAMILY_GROUP' }).CREATE;

  const { jobFamilies, getFamilies, totalCount, setVariables, loading, remove } = useJobFamilies();
  const familyPermissions = useStaticPermission('JOB_FAMILY_GROUP', true);
  const resultMessage = loading
    ? intl.loading
    : intl.viewingJobFamilyGroups({
        totalCount,
        pageDataCount: jobFamilies.length
      });

  const handleShowDeleteDialog = useCallback(
    (uids: string[]) => {
      setIsBulkAction(false);
      deleteDialog.show(uids);
    },
    [deleteDialog]
  );

  const handleDeleteAll = useCallback(
    async (uids: string[]) => {
      await remove(uids);
      getFamilies();
    },
    [remove, getFamilies]
  );

  const handleConfirmDelete = useCallback(async () => {
    await handleDeleteAll(deleteDialog.uids);
    deleteDialog.close();
  }, [handleDeleteAll, deleteDialog]);

  const handleSelectBulkActions = useCallback(async (selectedRows: JobFamilyGroup[]) => {
    const erasableFamilyGroups: JobFamilyGroup[] = [];
    const selectedWDFamilyGroups: JobFamilyGroup[] = [];
    selectedRows.forEach((jobFamilyGroup) => {
      if (canDelete(jobFamilyGroup.permissions.JOB_FAMILY_GROUP)) {
        erasableFamilyGroups.push(jobFamilyGroup);
      } else {
        selectedWDFamilyGroups.push(jobFamilyGroup);
      }
    });
    setSelectedWDJobFamilyGroups(selectedWDFamilyGroups);
    setSelectedJobFamilyGroups(erasableFamilyGroups);
  }, []);

  const handleEditJobFamilyGroup = useCallback(
    async (uid: string) => {
      setEditableJobFamilyGroup(jobFamilies?.find?.((jobFamily) => jobFamily.uid === uid) || null);
      getFamilies();
    },
    [jobFamilies, getFamilies]
  );

  const columns = useJobFamilyGroupColumns({
    onUpdated: handleEditJobFamilyGroup,
    onDeleteAll: handleShowDeleteDialog
  });

  if (!familyPermissions.READ) {
    return <>{intl.noPermissionToViewJobFamilyGroups}</>;
  }

  return (
    <>
      <DataTable
        onVariablesChanged={setVariables}
        data={jobFamilies}
        onColumnConfigurationChanged={patchColumnConfiguration}
        initialState={columnConfiguration}
        columns={columns}
        isFetching={loading}
        tableHeight="content"
        totalCount={totalCount}
        dataTest="job-family-groups"
        selectable={extendJobFamilyGroup && familyPermissions.DELETE ? 'uid' : undefined}
      >
        <DataTableBulkActions<JobFamilyGroup> onSelect={handleSelectBulkActions}>
          <DeleteBulkAction<JobFamilyGroup>
            disabled={selectedJobFamilyGroups.length === 0 || !extendJobFamilyGroup}
            disabledMessage={intl.cannotDeleteJobFamilyGroups}
            tooltipText={intl.delete}
            onClick={() => {
              setIsBulkAction(true);
              deleteDialog.show(rowsToUids(selectedJobFamilyGroups));
            }}
          />
        </DataTableBulkActions>
        <DataTableToolbar
          afterActions={
            <>
              <IconButton
                iconName="download"
                dataTest="import_job-family-group"
                disabled={!importJobFamilyGroupEnabled}
                onClick={() => setJobFamilyGroupImport(true)}
                tooltipText={intl.import}
              />
              {extendJobFamilyGroup && (
                <IconButton
                  iconName="add"
                  dataTest="add_job-family-group"
                  disabled={!addJobFamilyGroupEnabled}
                  onClick={() => setJobFamilyGroupAdd(true)}
                  tooltipText={intl.add}
                />
              )}
              {showJobFamilyGroupAdd && (
                <JobFamilyGroupDialog
                  onClose={() => setJobFamilyGroupAdd(false)}
                  onUpdated={() => {
                    setJobFamilyGroupAdd(false);
                    getFamilies();
                  }}
                />
              )}
              {importJobFamilyGroupEnabled && showJobFamilyGroupImport && (
                <Importer
                  allowedImportTypes={[{ id: 'JOB_FAMILY_GROUP', entityType: ImportEntityTypeModel.JobFamilyGroup }]}
                  key="JOB_FAMILY_GROUP"
                  setModal={setJobFamilyGroupImport}
                />
              )}
            </>
          }
          searchProps={{}}
          message={resultMessage}
        />

        <DataTablePaginator />
        <JobFamilyGroupsExporter />
      </DataTable>
      <JobFamilyGroupDeleteDialog
        show={deleteDialog.visible}
        isLoading={loading}
        isBulkAction={isBulkAction}
        jobFamilyGroups={selectedJobFamilyGroups}
        workdayJobFamilyGroups={selectedWDJobFamilyGroups}
        uids={deleteDialog.uids}
        onSubmit={handleConfirmDelete}
        onClose={() => deleteDialog.close()}
      />
    </>
  );
};

function rowsToUids(selectedRows: { uid?: string }[]): string[] {
  return selectedRows.map((row) => row?.uid ?? '');
}
